import type { Database } from "@repo/database";
import type { RealtimePostgresChangesPayload } from "@supabase/supabase-js";
import type { ReactNode } from "react";
import { createContext, useCallback, useEffect } from "react";
import { ProductTypes, Status } from "@repo/database";
import { parseISO } from "date-fns";
import { toast } from "sonner";

import type { RealtimeNotification } from "./notification-provider";
import type { VoysIncomingCall } from "~/types";
import { useUser } from "~/hooks/use-user";
import { api } from "~/lib/api";
import { supabase } from "~/lib/supabase";
import { useBookingUpdates } from "~/managers/use-booking-manager";
import { useButtonUpdates } from "~/managers/use-button-manager";
import { useEmployeeGroupUpdates } from "~/managers/use-employee-group-manager";
import { useEmployeeUpdates } from "~/managers/use-employee-manager";
import { useNotificationManager } from "~/managers/use-notification-manager";
import { useOrderChartUpdates } from "~/managers/use-order-chart-manager";
import { useOrderUpdates } from "~/managers/use-order-manager";
import { usePaymentMethodUpdates } from "~/managers/use-payment-method-manager";
import { useProductCategoryUpdates } from "~/managers/use-product-category-manager";
import { useProductUpdates } from "~/managers/use-product-manager";
import { useProductModifierGroupUpdates } from "~/managers/use-product-modifier-group-manager";
import { useProductModifierUpdates } from "~/managers/use-product-modifier-manager";
import { useTableUpdates } from "~/managers/use-table-manager";
import { useDeviceStore } from "~/stores/use-device-store";
import { useSectionUpdates } from "../managers/use-section-manager";

interface RealtimeProviderProps {
  children?: ReactNode;
}

const RealtimeContext = createContext(undefined);

export function RealtimeProvider({ children }: RealtimeProviderProps) {
  const { user } = useUser();

  const { deviceId } = useDeviceStore();

  const { handleTableDelete, handleTableUpdate } = useTableUpdates();

  const { handlePaymentMethodDelete, handlePaymentMethodUpdate } =
    usePaymentMethodUpdates();

  const { showNotification } = useNotificationManager();

  const { handleEmployeeDelete, handleEmployeeUpdate } = useEmployeeUpdates();

  const { handleEmployeeGroupDelete, handleEmployeeGroupUpdate } =
    useEmployeeGroupUpdates();

  const { handleBookingUpdate, handleBookingDelete } = useBookingUpdates();

  const { handleOrderDelete, handleOrderUpdate, handleOnlineOrderUpdate } =
    useOrderUpdates();

  const { handleProductDelete, handleProductUpdate } = useProductUpdates();
  const { handleProductModifierUpdate, handleProductModifierDelete } =
    useProductModifierUpdates();

  const { handleButtonDelete, handleButtonUpdate } = useButtonUpdates();

  const { handleProductModifierGroupUpdate, handleProductModifierGroupDelete } =
    useProductModifierGroupUpdates();

  const { handleProductCategoryUpdate, handleProductCategoryDelete } =
    useProductCategoryUpdates();

  const { handleOrderChartUpdate, handleOrderChartDelete } =
    useOrderChartUpdates();

  const { handleSectionUpdate, handleSectionDelete } = useSectionUpdates();

  const utils = api.useUtils();

  const handleRealtimeButtonUpdate = useCallback(
    (
      payload: RealtimePostgresChangesPayload<
        Database["public"]["Tables"]["button"]["Row"]
      >,
    ) => {
      if (payload.eventType === "UPDATE" || payload.eventType === "INSERT") {
        handleButtonUpdate(payload.new.id).catch((e) => console.error(e));
      } else {
        if (payload.old.id) {
          handleButtonDelete(payload.old.id);
        }
      }
    },
    [handleButtonDelete, handleButtonUpdate],
  );

  const handleRealtimeCourseUpdate = useCallback(
    (
      payload: RealtimePostgresChangesPayload<
        Database["public"]["Tables"]["course"]["Row"]
      >,
    ) => {
      if (payload.eventType === "UPDATE" || payload.eventType === "INSERT") {
        handleOrderChartUpdate(payload.new.id).catch((e) => console.error(e));
      } else {
        if (payload.old.id) {
          handleOrderChartDelete(payload.old.id);
        }
      }
    },
    [handleOrderChartUpdate, handleOrderChartDelete],
  );

  const handleRealtimeSectionUpdate = useCallback(
    (
      payload: RealtimePostgresChangesPayload<
        Database["public"]["Tables"]["location"]["Row"]
      >,
    ) => {
      if (payload.eventType === "UPDATE" || payload.eventType === "INSERT") {
        handleSectionUpdate(payload.new.id).catch((e) => console.error(e));
      } else {
        if (payload.old.id) {
          handleSectionDelete(payload.old.id);
        }
      }
    },
    [handleSectionUpdate, handleSectionDelete],
  );

  const handleRealtimeTableUpdate = useCallback(
    (
      payload: RealtimePostgresChangesPayload<
        Database["public"]["Tables"]["table"]["Row"]
      >,
    ) => {
      if (payload.eventType === "UPDATE" || payload.eventType === "INSERT") {
        handleTableUpdate(payload.new.id).catch((e) => console.error(e));
      } else {
        if (payload.old.id) {
          handleTableDelete(payload.old.id);
        }
      }
    },
    [handleTableUpdate, handleTableDelete],
  );

  const handleRealtimeProductCategoryUpdate = useCallback(
    (
      payload: RealtimePostgresChangesPayload<
        Database["public"]["Tables"]["product_category"]["Row"]
      >,
    ) => {
      if (payload.eventType === "UPDATE" || payload.eventType === "INSERT") {
        if (payload.new.status === Status.trashed) {
          handleProductCategoryDelete(payload.new.id);
        } else {
          handleProductCategoryUpdate(payload.new.id).catch((e) =>
            console.error(e),
          );
        }
      } else {
        if (payload.old.id) {
          handleProductCategoryDelete(payload.old.id);
        }
      }
    },
    [handleProductCategoryDelete, handleProductCategoryUpdate],
  );

  const handleRealtimeProductUpdate = useCallback(
    (
      payload: RealtimePostgresChangesPayload<
        Database["public"]["Tables"]["product"]["Row"]
      >,
    ) => {
      if (payload.eventType === "UPDATE" || payload.eventType === "INSERT") {
        if (payload.new.status === Status.trashed) {
          if (payload.new.type === ProductTypes.modifier) {
            handleProductModifierDelete(payload.new.id);
          } else {
            handleProductDelete(payload.new.id);
          }
        } else {
          if (payload.new.type === ProductTypes.modifier) {
            handleProductModifierUpdate(payload.new.id).catch((e) =>
              console.error(e),
            );
          } else {
            handleProductUpdate(payload.new.id).catch((e) => console.error(e));
          }
        }
      } else {
        if (payload.old.id) {
          handleProductDelete(payload.old.id);
          handleProductModifierDelete(payload.old.id);
        }
      }
    },
    [
      handleProductDelete,
      handleProductModifierDelete,
      handleProductModifierUpdate,
      handleProductUpdate,
    ],
  );

  const handleRealtimeProductModifierGroupUpdate = useCallback(
    (
      payload: RealtimePostgresChangesPayload<
        Database["public"]["Tables"]["product_modifier_group"]["Row"]
      >,
    ) => {
      if (payload.eventType === "UPDATE" || payload.eventType === "INSERT") {
        if (payload.new.status === Status.trashed) {
          handleProductModifierGroupDelete(payload.new.id);
        } else {
          handleProductModifierGroupUpdate(payload.new.id).catch((e) =>
            console.error(e),
          );
        }
      } else {
        if (payload.old.id) {
          handleProductModifierGroupDelete(payload.old.id);
        }
      }
    },
    [handleProductModifierGroupDelete, handleProductModifierGroupUpdate],
  );

  const handleRealtimeEmployeeUpdate = useCallback(
    (
      payload: RealtimePostgresChangesPayload<
        Database["public"]["Tables"]["employee"]["Row"]
      >,
    ) => {
      if (payload.eventType === "UPDATE" || payload.eventType === "INSERT") {
        if (payload.new.status === Status.trashed) {
          handleEmployeeDelete(payload.new.id);
        } else {
          handleEmployeeUpdate(payload.new.id).catch((e) => console.error(e));
        }
      } else {
        if (payload.old.id) {
          handleEmployeeDelete(payload.old.id);
        }
      }
    },
    [handleEmployeeDelete, handleEmployeeUpdate],
  );

  const handlePermissionsForEmployeeGroup = useCallback(
    (
      payload: RealtimePostgresChangesPayload<
        Database["public"]["Tables"]["permission_to_employee_group"]["Row"]
      >,
    ) => {
      if (payload.eventType === "UPDATE" || payload.eventType === "INSERT") {
        handleEmployeeGroupUpdate(payload.new.employee_group_id).catch((e) =>
          console.error(e),
        );
      } else {
        // TODO: fix this
        void utils.employeeGroup.getAll.invalidate();
      }
    },
    [handleEmployeeGroupUpdate],
  );

  const handleRealtimeEmployeeGroups = useCallback(
    (
      payload: RealtimePostgresChangesPayload<
        Database["public"]["Tables"]["employee_group"]["Row"]
      >,
    ) => {
      if (payload.eventType === "UPDATE" || payload.eventType === "INSERT") {
        if (payload.new.status === Status.trashed) {
          handleEmployeeGroupDelete(payload.new.id);
        } else {
          handleEmployeeGroupUpdate(payload.new.id).catch((e) =>
            console.error(e),
          );
        }
      } else {
        if (payload.old.id) {
          handleEmployeeGroupDelete(payload.old.id);
        }
      }
    },
    [handleEmployeeGroupDelete, handleEmployeeGroupUpdate],
  );

  const handleRealtimePaymentMethodUpdate = useCallback(
    (
      payload: RealtimePostgresChangesPayload<
        Database["public"]["Tables"]["payment_method"]["Row"]
      >,
    ) => {
      if (payload.eventType === "UPDATE" || payload.eventType === "INSERT") {
        if (payload.new.status === Status.trashed) {
          handlePaymentMethodDelete(payload.new.id);
        } else {
          handlePaymentMethodUpdate(payload.new.id).catch((e) =>
            console.error(e),
          );
        }
      } else {
        if (payload.old.id) {
          handlePaymentMethodDelete(payload.old.id);
        }
      }
    },
    [handlePaymentMethodDelete, handlePaymentMethodUpdate],
  );

  const handleRealtimeMollieConfiguration = useCallback(() => {
    void utils.config.getMollieSettings.invalidate();
  }, []);

  const handleRealtimeCashRegisterConfigurationUpdate = useCallback(() => {
    void utils.config.getCashRegisterConfiguration.invalidate();
  }, []);

  const handleRealtimeReceiptSettingsUpdate = useCallback(() => {
    void utils.config.getReceiptSettings.invalidate();
  }, []);

  const handleRealtimeBookingConfigurationUpdate = useCallback(() => {
    void utils.config.getBookingConfiguration.invalidate();
  }, []);

  const handleRealtimePrinterUpdate = useCallback(() => {
    void utils.printer.getAllPrinters.invalidate();
  }, []);

  const handleRealtimePrintRouteUpdate = useCallback(() => {
    void utils.printer.getAllPrintRoutes.invalidate();
  }, []);

  const handleRealtimeShiftUpdate = useCallback(() => {
    void utils.shift.getAll.invalidate();
  }, []);

  const handleRealtimeVatRateUpdate = useCallback(() => {
    void utils.vatRate.getAll.invalidate();
  }, []);

  const handleRealtimePriceUpdate = useCallback(() => {
    void utils.price.getAll.invalidate();
  }, []);

  const handleRealtimeProductOnProductCategoryUpdate = useCallback(() => {
    void utils.productCategory.getAll.invalidate();
  }, []);

  const handleRealtimeLabelUpdates = useCallback(() => {
    void utils.label.getAll.invalidate();
  }, []);

  const handleRealtimeModuleUpdate = useCallback(() => {
    void utils.module.getModules.invalidate();
  }, []);

  const handleRealtimePaymentUpdate = useCallback(() => {
    utils.order.getPayments.invalidate().catch((e) => {
      console.error(e);
    });
  }, []);

  const handleRealtimePermissionToEmployeeGroupUpdate = useCallback(() => {
    utils.employeeGroup.getAll.invalidate().catch((e) => {
      console.error(e);
    });
  }, []);

  const handleRealtimeUserUpdate = useCallback(() => {
    utils.user.getUser.invalidate().catch((e) => {
      console.error(e);
    });
  }, []);

  const handleRealtimeServiceUpdate = useCallback(() => {
    utils.service.getAll.invalidate().catch((e) => {
      console.error(e);
    });
  }, []);

  useEffect(() => {
    const statusChannel = supabase
      .channel("status-channel")
      .on("broadcast", { event: "connect" }, () => {})
      .subscribe();

    const broadCastChannel = supabase
      .channel(`broadcast-channel-${user.id}`)
      .on<RealtimeNotification>(
        "broadcast",
        {
          event: "new-notification",
        },
        (payload) => {
          showNotification({
            id: payload.payload.id,
            type: payload.payload.type,
            seenAt: payload.payload.seenAt
              ? parseISO(payload.payload.seenAt)
              : undefined,
            archivedAt: payload.payload.archivedAt
              ? parseISO(payload.payload.archivedAt)
              : undefined,
            booking: payload.payload.booking,
            createdAt: parseISO(payload.payload.createdAt),
          });
        },
      )
      .on<VoysIncomingCall>(
        "broadcast",
        {
          event: "incoming-call",
        },
        (payload) => {
          toast.info(`Incoming call from ${payload.payload.callerId},`, {
            duration: 10000,
          });
        },
      )
      .on<{ bookingId: string }>(
        "broadcast",
        {
          event: "widget-booking",
        },
        (payload) => {
          handleBookingUpdate(payload.payload.bookingId).catch((e) =>
            console.error(e),
          );
        },
      )
      .on<{ bookingId: string; deviceId: string }>(
        "broadcast",
        {
          event: "booking-update",
        },
        (payload) => {
          if (payload.payload.deviceId === deviceId) return;
          handleBookingUpdate(payload.payload.bookingId).catch((e) =>
            console.error(e),
          );
        },
      )
      .on<{ bookingId: string; deviceId: string }>(
        "broadcast",
        {
          event: "booking-delete",
        },
        (payload) => {
          if (payload.payload.deviceId === deviceId) return;
          handleBookingDelete(payload.payload.bookingId);
        },
      )
      .on<{ orderId: string }>(
        "broadcast",
        {
          event: "new-online-order",
        },
        (payload) => {
          handleOnlineOrderUpdate(payload.payload.orderId).catch((err) => {
            console.error(err);
          });
        },
      )
      .on<{ orderId: string; deviceId: string }>(
        "broadcast",
        {
          event: "order-status-updated",
        },
        (payload) => {
          if (payload.payload.deviceId === deviceId) return;
          handleOnlineOrderUpdate(payload.payload.orderId).catch((e) =>
            console.error(e),
          );
        },
      )
      .on<{ orderId: string; deviceId: string }>(
        "broadcast",
        {
          event: "order-update",
        },
        (payload) => {
          if (payload.payload.deviceId === deviceId) return;
          handleOrderUpdate(payload.payload.orderId).catch((e) =>
            console.error(e),
          );
        },
      )
      .on<{ newOrderId: string; oldOrderId: string; deviceId: string }>(
        "broadcast",
        {
          event: "order-split",
        },
        (payload) => {
          if (payload.payload.deviceId === deviceId) return;
          handleOrderUpdate(payload.payload.newOrderId).catch((e) =>
            console.error(e),
          );
          handleOrderUpdate(payload.payload.oldOrderId).catch((e) =>
            console.error(e),
          );
        },
      )
      .on<{ orderId: string; deviceId: string }>(
        "broadcast",
        {
          event: "order-finalize",
        },
        (payload) => {
          if (payload.payload.deviceId === deviceId) return;
          handleOrderDelete(payload.payload.orderId);
        },
      )
      .on<{ orderId: string; deviceId: string }>(
        "broadcast",
        {
          event: "order-delete",
        },
        (payload) => {
          if (payload.payload.deviceId === deviceId) return;
          handleOrderDelete(payload.payload.orderId);
        },
      )
      .on<{ oldOrderId?: string; newOrderId: string; deviceId: string }>(
        "broadcast",
        {
          event: "order-transfer",
        },
        (payload) => {
          if (payload.payload.deviceId === deviceId) return;
          handleOrderUpdate(payload.payload.newOrderId).catch((e) =>
            console.error(e),
          );
        },
      )
      .subscribe();

    const realtimeChannel = supabase
      .channel("realtime-updates")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "user",
          filter: `id=eq.${user.id}`,
        },
        handleRealtimeUserUpdate,
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "product",
          filter: `user_id=eq.${user.id}`,
        },
        handleRealtimeProductUpdate,
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "permission_to_employee_group",
          filter: `user_id=eq.${user.id}`,
        },
        handleRealtimePermissionToEmployeeGroupUpdate,
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "employee",
          filter: `user_id=eq.${user.id}`,
        },
        handleRealtimeEmployeeUpdate,
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "payment",
          filter: `user_id=eq.${user.id}`,
        },
        handleRealtimePaymentUpdate,
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "product_category_on_product",
          filter: `user_id=eq.${user.id}`,
        },
        handleRealtimeProductOnProductCategoryUpdate,
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "product_category",
          filter: `user_id=eq.${user.id}`,
        },
        handleRealtimeProductCategoryUpdate,
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "module",
        },
        handleRealtimeModuleUpdate,
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "label",
          filter: `user_id=eq.${user.id}`,
        },
        handleRealtimeLabelUpdates,
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "location",
          filter: `user_id=eq.${user.id}`,
        },
        handleRealtimeSectionUpdate,
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "price",
          filter: `user_id=eq.${user.id}`,
        },
        handleRealtimePriceUpdate,
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "course",
          filter: `user_id=eq.${user.id}`,
        },
        handleRealtimeCourseUpdate,
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "permission_to_employee_group",
          filter: `user_id=eq.${user.id}`,
        },
        handlePermissionsForEmployeeGroup,
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "employee_group",
          filter: `user_id=eq.${user.id}`,
        },
        handleRealtimeEmployeeGroups,
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "shift",
          filter: `user_id=eq.${user.id}`,
        },
        handleRealtimeShiftUpdate,
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "service",
          filter: `user_id=eq.${user.id}`,
        },
        handleRealtimeServiceUpdate,
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "print_route",
          filter: `user_id=eq.${user.id}`,
        },
        handleRealtimePrintRouteUpdate,
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "printer",
          filter: `user_id=eq.${user.id}`,
        },
        handleRealtimePrinterUpdate,
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "product_modifier_group",
          filter: `user_id=eq.${user.id}`,
        },
        handleRealtimeProductModifierGroupUpdate,
      )
      // .on(
      //   "postgres_changes",
      //   {
      //     event: "*",
      //     schema: "public",
      //     table: "product_usable_modifier_group",
      //     filter: `user_id=eq.${user.id}`,
      //   },
      //   handleRealtimeProductModifierGroupUpdate,
      // )
      // .on(
      //   "postgres_changes",
      //   {
      //     event: "*",
      //     schema: "public",
      //     table: "product_modifier_group_membership",
      //     filter: `user_id=eq.${user.id}`,
      //   },
      //   handleRealtimeProductModifierGroupUpdate,
      // )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "button",
          filter: `user_id=eq.${user.id}`,
        },
        handleRealtimeButtonUpdate,
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "booking_configuration",
          filter: `user_id=eq.${user.id}`,
        },
        handleRealtimeBookingConfigurationUpdate,
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "cash_register_configuration",
          filter: `user_id=eq.${user.id}`,
        },
        handleRealtimeCashRegisterConfigurationUpdate,
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "receipt_settings",
          filter: `user_id=eq.${user.id}`,
        },
        handleRealtimeReceiptSettingsUpdate,
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "payment_method",
          filter: `user_id=eq.${user.id}`,
        },
        handleRealtimePaymentMethodUpdate,
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "table",
          filter: `user_id=eq.${user.id}`,
        },
        handleRealtimeTableUpdate,
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "vat_rate",
          filter: `user_id=eq.${user.id}`,
        },
        handleRealtimeVatRateUpdate,
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "mollie_configuration",
          filter: `user_id=eq.${user.id}`,
        },
        handleRealtimeMollieConfiguration,
      )
      .subscribe();

    return () => {
      console.log("UNSUB");
      supabase.removeChannel(realtimeChannel).catch((e) => {
        console.error(e);
      });
      supabase.removeChannel(broadCastChannel).catch((e) => {
        console.error(e);
      });
      supabase.removeChannel(statusChannel).catch((e) => {
        console.error(e);
      });
    };
  }, [
    handleRealtimeCashRegisterConfigurationUpdate,
    handleRealtimeMollieConfiguration,
    handleRealtimeShiftUpdate,
    handleRealtimeVatRateUpdate,
    handleRealtimeProductOnProductCategoryUpdate,
    handleRealtimeModuleUpdate,
    handleRealtimePriceUpdate,
    handleRealtimePaymentUpdate,
    handleRealtimePrintRouteUpdate,
    handleRealtimePrinterUpdate,
    handleRealtimeUserUpdate,
    handleRealtimeServiceUpdate,
    handleRealtimeSectionUpdate,
    handleRealtimeProductCategoryUpdate,
    handleRealtimeTableUpdate,
    handleRealtimeCourseUpdate,
    handleRealtimeButtonUpdate,
    handleRealtimeEmployeeGroups,
    handleRealtimeLabelUpdates,
    handleRealtimeBookingConfigurationUpdate,
    handlePermissionsForEmployeeGroup,
    handleRealtimeProductUpdate,
    handleRealtimeReceiptSettingsUpdate,
    handleRealtimePermissionToEmployeeGroupUpdate,
    handleRealtimePaymentMethodUpdate,
    handleRealtimeEmployeeUpdate,
    handleRealtimeProductModifierGroupUpdate,
    user.id,
  ]);

  return (
    <RealtimeContext.Provider value={undefined}>
      {children}
    </RealtimeContext.Provider>
  );
}
